import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { careerAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, TrendingUp, RefreshCw, Target, 
  ChevronRight, Clock, BookOpen, Award, DollarSign,
  CheckCircle, ArrowRight, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const CareerPathPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [paths, setPaths] = useState([]);
  const [currentRole, setCurrentRole] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [careerPlan, setCareerPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPaths, setLoadingPaths] = useState(true);

  useEffect(() => {
    fetchPaths();
  }, []);

  const fetchPaths = async () => {
    try {
      const res = await careerAPI.getPaths();
      setPaths(res.data.paths || []);
    } catch (error) {
      console.error('Failed to fetch paths:', error);
    } finally {
      setLoadingPaths(false);
    }
  };

  const generatePlan = async () => {
    if (!currentRole.trim()) {
      toast.error('Please enter your current role');
      return;
    }

    setLoading(true);
    try {
      const res = await careerAPI.getPlan({
        current_role: currentRole,
        target_role: targetRole || null
      });
      setCareerPlan(res.data);
    } catch (error) {
      toast.error('Failed to generate career plan');
    } finally {
      setLoading(false);
    }
  };

  const selectPath = (path) => {
    setCurrentRole(path.title || path.entry_role || '');
    setTargetRole('');
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Career Path Planner</h2>
          <p className="text-slate-600 mb-6">Map your career journey. Subscribe to access this feature.</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Career Path Planner</h1>
            <p className="text-sm text-slate-500">Map your journey to your dream career</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 lg:p-8">
        {/* Popular Paths */}
        {!careerPlan && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Popular Career Paths in SA</h2>
            {loadingPaths ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {paths.map((path, i) => (
                  <button
                    key={i}
                    onClick={() => selectPath(path)}
                    className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-blue-500 hover:shadow-md transition-all group"
                    data-testid={`path-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-semibold text-slate-900">{path.title || path.entry_role || 'Career Path'}</span>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Start: {path.current_salary || path.salary_range || ''}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-emerald-600 font-medium">Max: {path.max_potential || ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Custom Path Input */}
        {!careerPlan && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
            <h3 className="font-semibold text-slate-900 mb-4">Or Create Your Own Path</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Current/Starting Role
                </label>
                <input
                  type="text"
                  value={currentRole}
                  onChange={(e) => setCurrentRole(e.target.value)}
                  placeholder="e.g., Receptionist"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="current-role-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Target Role (Optional)
                </label>
                <input
                  type="text"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  placeholder="e.g., Office Manager"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="target-role-input"
                />
              </div>
            </div>
            <Button
              onClick={generatePlan}
              disabled={loading || !currentRole.trim()}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
              data-testid="generate-plan-btn"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <TrendingUp className="w-5 h-5 mr-2" />
              )}
              Generate Career Plan
            </Button>
          </div>
        )}

        {/* Career Plan Results */}
        {careerPlan && (
          <div className="space-y-6">
            {/* Reset Button */}
            <Button
              variant="outline"
              onClick={() => {
                setCareerPlan(null);
                setCurrentRole('');
                setTargetRole('');
              }}
              data-testid="reset-plan-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Start Over
            </Button>

            {/* Career Path Overview */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
              <h2 className="text-xl font-bold mb-2">Your Career Path</h2>
              <p className="text-blue-100 mb-4">
                Current: <span className="font-semibold">{careerPlan.current_role}</span>
                {careerPlan.current_salary && <span> • {careerPlan.current_salary}</span>}
              </p>
              
              {/* Path Visualization - Use 'progression' from API */}
              <div className="flex items-center gap-3 overflow-x-auto pb-2">
                {(careerPlan.progression || []).map((step, i) => (
                  <React.Fragment key={i}>
                    <div className="bg-white/20 rounded-xl px-4 py-3 flex-shrink-0">
                      <p className="text-xs text-blue-100">Step {i + 1}</p>
                      <p className="font-semibold">{step.role}</p>
                      {step.salary && (
                        <p className="text-sm text-blue-100">{step.salary}</p>
                      )}
                      {step.time && (
                        <p className="text-xs text-blue-200 mt-1">{step.time}</p>
                      )}
                    </div>
                    {i < (careerPlan.progression || []).length - 1 && (
                      <ChevronRight className="w-6 h-6 text-white/50 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Skills to Develop from progression */}
            {careerPlan.progression && careerPlan.progression.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Skills You'll Need</h3>
                </div>
                
                <div className="space-y-4">
                  {careerPlan.progression.map((step, i) => (
                    <div key={i} className="p-4 bg-slate-50 rounded-xl">
                      <p className="font-semibold text-slate-900 mb-2">For {step.role}:</p>
                      <div className="flex flex-wrap gap-2">
                        {(step.skills_needed || []).map((skill, j) => (
                          <span key={j} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tip */}
            {careerPlan.tip && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Lightbulb className="w-6 h-6 text-amber-600" />
                  <h3 className="text-lg font-bold text-amber-800">Pro Tip</h3>
                </div>
                <p className="text-amber-800">{careerPlan.tip}</p>
              </div>
            )}

            {/* General Advice */}
            {careerPlan.general_advice && careerPlan.general_advice.length > 0 && (
              <div className="bg-slate-900 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-6 h-6 text-emerald-400" />
                  <h3 className="text-lg font-bold">Career Advice</h3>
                </div>
                <ul className="space-y-2">
                  {careerPlan.general_advice.map((advice, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-200">{advice}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default CareerPathPage;
